import {Button, Card, Collapse, Flex, Table, Typography} from "antd";
import type {CollapseProps} from "antd";
import type {ColumnsType} from "antd/es/table";
import {RightOutlined} from "@ant-design/icons";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import {useMemo, useCallback} from "react";
import {useNavigate} from "react-router";
import {IAdmissionStatement, IAdmissionTraining, IAdmissionTrainingProfile} from "./model/admissionModel";

const {Text} = Typography;

interface IProfileDisplay {
    name: string;
    priority: string;
}

function AdmissionProfileRows({profiles}: {profiles: IProfileDisplay[]}) {
    if (!profiles?.length) {
        return null;
    }

    return (
        <Flex vertical gap={4}>
            {profiles.map((row, i) => (
                <Text type="secondary" key={`${row.priority}-${i}`}>
                    {row.priority}. {row.name}
                </Text>
            ))}
        </Flex>
    );
}

function getProfilePriority(profile: string | IAdmissionTrainingProfile, index: number, trainingPriority?: number): string {
    if (typeof profile !== "string") {
        const p = profile?.priority;
        if (p != null) {
            return trainingPriority != null ? `${trainingPriority}.${p}` : String(p);
        }
    }
    return trainingPriority != null ? `${trainingPriority}.${index + 1}` : `${index + 1}`;
}

function getProfileName(profile: string | IAdmissionTrainingProfile): string {
    if (typeof profile === "string") return profile.trim();
    if (!profile) return "";
    return (
        profile.name ||
        profile.display ||
        profile.ProfileName ||
        profile.profileName ||
        profile.title ||
        ""
    ).trim();
}

function normalizeTrainingProfiles(row: IAdmissionTraining, trainingPriority?: number): IProfileDisplay[] {
    const profileArray = row.profile;
    if (Array.isArray(profileArray) && profileArray.length > 0) {
        return profileArray
            .map((item, i) => {
                const name = getProfileName(item);
                if (!name) return null;
                const priority = getProfilePriority(item, i, trainingPriority ?? row.priority);
                return {name, priority};
            })
            .filter((item): item is IProfileDisplay => item !== null);
    }
    if (Array.isArray(row.profiles)) {
        return row.profiles
            .map((line, i) => {
                const trimmed = line.trim();
                if (!trimmed) return null;
                const m = trimmed.match(/^\s*(\d+)\.(\d+)\.?\s*(.*)$/);
                if (m) {
                    const name = (m[3] ?? "").trim();
                    return {name: name || trimmed, priority: `${m[1]}.${m[2]}`};
                }
                const tp = trainingPriority ?? row.priority;
                return {name: trimmed, priority: tp != null ? `${tp}.${i + 1}` : `${i + 1}`};
            })
            .filter((item): item is IProfileDisplay => item !== null);
    }
    return [];
}

function SpecialtyCell({row, trainingPriority}: {row: IAdmissionTraining; trainingPriority?: number}) {
    const title = row.specName
        ? `${row.specQualifier} ${row.specName}`.trim()
        : row.specQualifier;
    const profiles = normalizeTrainingProfiles(row, trainingPriority ?? row.priority);

    return (
        <div className="admission-specialty-cell">
            <Text strong>{title}</Text>
            {row.TypeKG && <Text type="secondary" style={{display: "block"}}>{row.TypeKG}</Text>}
            {row.TypeKG && profiles.length > 0 && <div style={{borderTop: "1px solid #e8e8e8", margin: "6px 0"}} />}
            <AdmissionProfileRows profiles={profiles}/>
        </div>
    );
}


function MobileTrainingList({
    training,
    showContractColumn,
}: {
    training: IAdmissionTraining[];
    showContractColumn: boolean;
}) {
    return (
        <Flex vertical gap={12} className="admission-training-list-mobile">
            {training.map((row, index) => (
                <Card key={`${row.specQualifier}-${index}`} size="small" className="admission-training-card">
                    <Flex vertical gap={8}>
                        <Flex vertical gap={2}>
                            <Text strong>Приоритет</Text>
                            <Text>{row.priority ?? index + 1}</Text>
                        </Flex>
                        <Flex vertical gap={4}>
                            <Text strong>Специальность / направление</Text>
                            <SpecialtyCell row={row} trainingPriority={row.priority ?? index + 1}/>
                        </Flex>
                        <Flex vertical gap={2}>
                            <Text strong>Форма обучения</Text>
                            <Text>{row.kindsTraining}</Text>
                        </Flex>
                        <Flex vertical gap={2}>
                            <Text strong>Вид конкурса</Text>
                            <Text>{row.typeCompetition}</Text>
                        </Flex>
                        {showContractColumn && (
                            <Flex vertical gap={2}>
                                <Text strong>Договор об образовании</Text>
                                <Text>{row.contractSigned ? "Заключен" : "Не заключен"}</Text>
                            </Flex>
                        )}
                    </Flex>
                </Card>
            ))}
        </Flex>
    );
}

export interface AdmissionStatementItemProps {
    statement: IAdmissionStatement;
    /** Платная основа — колонка «Договор об образовании» */
    showContractColumn: boolean;
}

export default function AdmissionStatementItem({statement, showContractColumn}: AdmissionStatementItemProps) {
    const {xl} = useBreakpoint();
    const isMobile = !xl;
    const navigate = useNavigate();

    const handleEditPriorities = useCallback(() => {
        navigate("/admission/profiles/edit", {state: {statement}});
    }, [navigate, statement]);

    const canEditProfilePriorities = useMemo(() => {
        if (!statement.EditingPriorities) {
            return false;
        }
        return (statement.training ?? []).some(row => {
            const profileArray = row.profile;
            if (Array.isArray(profileArray)) {
                const validCount = profileArray.filter(item => {
                    if (typeof item === "string") return item.trim().length > 0;
                    if (!item) return false;
                    return Boolean(item.name || item.display || item.ProfileName || item.profileName || item.title);
                }).length;
                return validCount > 1;
            }
            return false;
        });
    }, [statement]);

    const statementComment = (statement.comment ?? statement.Comment ?? "").trim();

    const columns: ColumnsType<IAdmissionTraining> = useMemo(() => {
        const cols: ColumnsType<IAdmissionTraining> = [
            {
                title: "Приоритет",
                key: "priority",
                width: 100,
                align: "center",
                render: (_: unknown, row: IAdmissionTraining, index: number) => <Text strong>{row.priority ?? index + 1}</Text>,
            },
            {
                title: "Специальность/направление",
                key: "specialty",
                align: "center",
                className: "admission-specialty-column",
                render: (_: unknown, row: IAdmissionTraining, index: number) => (
                    <SpecialtyCell row={row} trainingPriority={row.priority ?? index + 1}/>
                ),
            },
            {
                title: "Форма обучения",
                key: "kindsTraining",
                align: "center",
                dataIndex: "kindsTraining",
            },
            {
                title: "Вид конкурса",
                key: "typeCompetition",
                align: "center",
                dataIndex: "typeCompetition",
            },
        ];

        if (showContractColumn) {
            cols.push({
                title: "Договор об образовании",
                key: "contractSigned",
                width: 200,
                align: "center",
                render: (_: unknown, row: IAdmissionTraining) => row.contractSigned ? "Заключен" : "Не заключен",
            });
        }

        return cols;
    }, [showContractColumn]);

    const collapseItems: CollapseProps["items"] = useMemo(
        () => [
            {
                key: "main",
                label: (
                    <Flex
                        className="admission-statement-collapse-label"
                        vertical={isMobile}
                        align={isMobile ? "stretch" : "center"}
                        justify="space-between"
                        gap={isMobile ? 12 : 16}
                    >
                        <Flex align="flex-start" className="admission-statement-header-left" flex={1}>
                            <Flex vertical gap={4} className="admission-statement-header-info">
                                <div>
                                    <Text strong>Тип заявления: </Text>
                                    <Text>{statement.Statement}</Text>
                                </div>
                                <div>
                                    <Text strong>Основа обучения: </Text>
                                    <Text>{statement.BasisTraining}</Text>
                                </div>
                            </Flex>
                        </Flex>
                        <Flex
                            vertical={isMobile}
                            gap={isMobile ? 12 : 16}
                            align={isMobile ? "stretch" : "center"}
                            className="admission-statement-header-actions"
                        >
                            {statementComment ? (
                                <Text className="admission-statement-comment">
                                    {statementComment}
                                </Text>
                            ) : null}
                            {canEditProfilePriorities && (isMobile ? (
                                <Flex justify="center" className="admission-statement-edit-btn-wrap">
                                    <Button
                                        className="admission-change-priority-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditPriorities();
                                        }}
                                    >
                                        Изменить приоритеты программ
                                    </Button>
                                </Flex>
                            ) : (
                                <Button
                                    className="admission-change-priority-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditPriorities();
                                    }}
                                >
                                    Изменить приоритеты программ
                                </Button>
                            ))}
                        </Flex>
                    </Flex>
                ),
                children: isMobile ? (
                    <MobileTrainingList training={statement.training ?? []} showContractColumn={showContractColumn}/>
                ) : (
                    <Table<IAdmissionTraining>
                        className="admission-statement-table"
                        columns={columns}
                        dataSource={statement.training ?? []}
                        pagination={false}
                        size="middle"
                        rowKey={(_, index) => String(index)}
                    />
                ),
            },
        ],
        [statement, isMobile, columns, handleEditPriorities, canEditProfilePriorities, statementComment],
    );

    return (
        <Collapse
            defaultActiveKey={["main"]}
            expandIconPosition="start"
            expandIcon={({isActive}) => (
                <span className={`admission-collapse-expand-icon${isActive ? " admission-collapse-expand-icon--open" : ""}`}>
                    <RightOutlined/>
                </span>
            )}
            className="admission-statement-collapse admission-statement-group"
            items={collapseItems}
        />
    );
}
